import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  PlayArrow as RunIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Email as MailIcon,
  CleaningServices as CleanupIcon,
  Sync as SyncIcon,
  Webhook as WebhookIcon,
  Build as CustomIcon,
  WbSunny as BriefingIcon,
} from '@mui/icons-material';
import { scheduledJobsApi, type ScheduledJob, type JobExecution, type CreateScheduledJobDto, type JobTypeInfo } from '../services/api';

// Icon-Mapping für Job-Typen (wird mit API-Daten kombiniert)
const JOB_TYPE_ICONS: Record<string, { icon: React.ReactElement; color: string }> = {
  'MailAbruf': { icon: <MailIcon />, color: 'primary' },
  'TokenCleanup': { icon: <CleanupIcon />, color: 'secondary' },
  'DokumentSync': { icon: <SyncIcon />, color: 'info' },
  'WebhookCall': { icon: <WebhookIcon />, color: 'warning' },
  'McpCall': { icon: <WebhookIcon />, color: 'success' },
  'Backup': { icon: <SyncIcon />, color: 'default' },
  'TagesBriefing': { icon: <BriefingIcon />, color: 'warning' },
  'Custom': { icon: <CustomIcon />, color: 'default' },
};

// Fallback für unbekannte Typen
const DEFAULT_ICON: { icon: React.ReactElement; color: string } = { icon: <CustomIcon />, color: 'default' };

const ScheduledJobsVerwaltung: React.FC = () => {
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [jobTypes, setJobTypes] = useState<JobTypeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [executionsDialogOpen, setExecutionsDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<ScheduledJob | null>(null);
  const [selectedJobForExecutions, setSelectedJobForExecutions] = useState<ScheduledJob | null>(null);
  const [executions, setExecutions] = useState<JobExecution[]>([]);
  const [loadingExecutions, setLoadingExecutions] = useState(false);
  const [runningJobId, setRunningJobId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [formData, setFormData] = useState<CreateScheduledJobDto>({
    jobName: '',
    jobTyp: 'MailAbruf',
    beschreibung: '',
    intervallMinuten: 60,
    startzeit: '',
    konfiguration: '',
    istAktiv: true,
  });

  useEffect(() => {
    loadJobs();
    loadJobTypes();
  }, []);

  const loadJobTypes = async () => {
    try {
      const response = await scheduledJobsApi.getTypes();
      setJobTypes(response.data);
    } catch (err) {
      console.error('JobTypes-Fehler:', err);
      // Fallback: Leere Liste - Icons werden trotzdem via JOB_TYPE_ICONS gemappt
    }
  };

  const loadJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await scheduledJobsApi.getAll();
      setJobs(response.data);
    } catch (err: any) {
      console.error('Jobs-Fehler:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (job?: ScheduledJob) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        jobName: job.jobName,
        jobTyp: job.jobTyp,
        beschreibung: job.beschreibung || '',
        intervallMinuten: job.intervallMinuten,
        startzeit: job.startzeit || '',
        konfiguration: job.konfiguration || '',
        istAktiv: job.istAktiv,
      });
    } else {
      setEditingJob(null);
      setFormData({
        jobName: '',
        jobTyp: 'MailAbruf',
        beschreibung: '',
        intervallMinuten: 60,
        startzeit: '',
        konfiguration: '',
        istAktiv: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingJob(null);
  };

  const handleSave = async () => {
    try {
      if (editingJob) {
        await scheduledJobsApi.update(editingJob.jobId, formData);
        setSnackbar({ open: true, message: 'Job erfolgreich aktualisiert', severity: 'success' });
      } else {
        await scheduledJobsApi.create(formData);
        setSnackbar({ open: true, message: 'Job erfolgreich erstellt', severity: 'success' });
      }
      handleCloseDialog();
      loadJobs();
    } catch (err: any) {
      console.error('Speicherfehler:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Fehler beim Speichern',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (job: ScheduledJob) => {
    if (!window.confirm(`Job "${job.jobName}" wirklich löschen?`)) return;

    try {
      await scheduledJobsApi.delete(job.jobId);
      setSnackbar({ open: true, message: 'Job erfolgreich gelöscht', severity: 'success' });
      loadJobs();
    } catch (err: any) {
      console.error('Löschfehler:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Fehler beim Löschen',
        severity: 'error',
      });
    }
  };

  const handleToggle = async (job: ScheduledJob) => {
    try {
      await scheduledJobsApi.toggle(job.jobId);
      setSnackbar({
        open: true,
        message: job.istAktiv ? 'Job deaktiviert' : 'Job aktiviert',
        severity: 'success',
      });
      loadJobs();
    } catch (err: any) {
      console.error('Toggle-Fehler:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Fehler beim Umschalten',
        severity: 'error',
      });
    }
  };

  const handleRunNow = async (job: ScheduledJob) => {
    try {
      setRunningJobId(job.jobId);
      await scheduledJobsApi.runNow(job.jobId);
      setSnackbar({ open: true, message: `Job "${job.jobName}" wird ausgeführt...`, severity: 'info' });
      // Kurz warten und dann neu laden um Status zu aktualisieren
      setTimeout(() => {
        loadJobs();
        setRunningJobId(null);
      }, 2000);
    } catch (err: any) {
      console.error('Ausführungsfehler:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Fehler beim Ausführen',
        severity: 'error',
      });
      setRunningJobId(null);
    }
  };

  const handleShowExecutions = async (job: ScheduledJob) => {
    setSelectedJobForExecutions(job);
    setExecutionsDialogOpen(true);
    setLoadingExecutions(true);

    try {
      const response = await scheduledJobsApi.getExecutions(job.jobId, 50);
      setExecutions(response.data);
    } catch (err: any) {
      console.error('Executions-Fehler:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Ausführungen',
        severity: 'error',
      });
    } finally {
      setLoadingExecutions(false);
    }
  };

  const getJobTypeInfo = (jobTyp: string) => {
    const apiType = jobTypes.find(t => t.value === jobTyp);
    const iconInfo = JOB_TYPE_ICONS[jobTyp] || DEFAULT_ICON;
    return {
      value: jobTyp,
      label: apiType?.label || jobTyp,
      icon: iconInfo.icon,
      color: iconInfo.color,
    };
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-CH');
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Success':
        return <SuccessIcon color="success" fontSize="small" />;
      case 'Failed':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'Running':
        return <CircularProgress size={16} />;
      case 'Cancelled':
        return <WarningIcon color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <ScheduleIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5">Geplante Jobs</Typography>
        </Box>
        <Box>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadJobs} sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Neuer Job
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Typ</TableCell>
              <TableCell>Intervall</TableCell>
              <TableCell>Letzte Ausführung</TableCell>
              <TableCell>Nächste Ausführung</TableCell>
              <TableCell>Fehler</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary" py={3}>
                    Keine geplanten Jobs vorhanden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => {
                const typeInfo = getJobTypeInfo(job.jobTyp);
                return (
                  <TableRow key={job.jobId}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={job.istAktiv ? 'Aktiv' : 'Inaktiv'}
                        color={job.istAktiv ? 'success' : 'default'}
                        onClick={() => handleToggle(job)}
                        sx={{ cursor: 'pointer' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="medium">{job.jobName}</Typography>
                      {job.beschreibung && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {job.beschreibung}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={typeInfo.icon}
                        label={typeInfo.label}
                        color={typeInfo.color as any}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {job.intervallMinuten < 60
                        ? `${job.intervallMinuten} Min.`
                        : job.intervallMinuten < 1440
                        ? `${Math.floor(job.intervallMinuten / 60)} Std.`
                        : `${Math.floor(job.intervallMinuten / 1440)} Tag(e)`}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(job.letzteAusfuehrung)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(job.naechsteAusfuehrung)}</Typography>
                    </TableCell>
                    <TableCell>
                      {job.fehlerAnzahl > 0 ? (
                        <Tooltip
                          title={
                            <Box sx={{ maxWidth: 400 }}>
                              <Typography variant="body2" fontWeight="bold" mb={0.5}>
                                Letzter Fehler:
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {job.letzterFehler || 'Unbekannter Fehler'}
                              </Typography>
                            </Box>
                          }
                        >
                          <Chip
                            size="small"
                            icon={<ErrorIcon />}
                            label={`${job.fehlerAnzahl} Fehler`}
                            color="error"
                            variant="outlined"
                            sx={{ cursor: 'help' }}
                          />
                        </Tooltip>
                      ) : job.letzteAusfuehrung ? (
                        <Chip
                          size="small"
                          icon={<SuccessIcon />}
                          label="OK"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nie ausgeführt
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Jetzt ausführen">
                        <IconButton
                          size="small"
                          onClick={() => handleRunNow(job)}
                          disabled={runningJobId === job.jobId || !job.istAktiv}
                        >
                          {runningJobId === job.jobId ? (
                            <CircularProgress size={20} />
                          ) : (
                            <RunIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Ausführungshistorie">
                        <IconButton size="small" onClick={() => handleShowExecutions(job)}>
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => handleOpenDialog(job)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton size="small" color="error" onClick={() => handleDelete(job)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Job erstellen/bearbeiten Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingJob ? 'Job bearbeiten' : 'Neuer geplanter Job'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Job-Name"
              value={formData.jobName}
              onChange={(e) => setFormData({ ...formData, jobName: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Job-Typ</InputLabel>
              <Select
                value={formData.jobTyp}
                onChange={(e) => setFormData({ ...formData, jobTyp: e.target.value })}
                label="Job-Typ"
              >
                {jobTypes.map((type) => {
                  const iconInfo = JOB_TYPE_ICONS[type.value] || DEFAULT_ICON;
                  return (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {iconInfo.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <TextField
              label="Beschreibung"
              value={formData.beschreibung}
              onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Intervall (Minuten)"
              type="number"
              value={formData.intervallMinuten}
              onChange={(e) =>
                setFormData({ ...formData, intervallMinuten: Math.max(1, parseInt(e.target.value) || 1) })
              }
              fullWidth
              inputProps={{ min: 1 }}
              helperText="Ausführungsintervall in Minuten (z.B. 60 = jede Stunde)"
            />
            <TextField
              label="Startzeit"
              type="time"
              value={formData.startzeit || ''}
              onChange={(e) => setFormData({ ...formData, startzeit: e.target.value || undefined })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Uhrzeit für die erste Ausführung (optional). Danach wiederholt sich der Job im Intervall."
            />
            <TextField
              label="Konfiguration (JSON)"
              value={formData.konfiguration}
              onChange={(e) => setFormData({ ...formData, konfiguration: e.target.value })}
              fullWidth
              multiline
              rows={3}
              placeholder='{"mailKontoIds": [1,2,3], "maxMailsProKonto": 50}'
              helperText="Job-spezifische Einstellungen als JSON-Objekt"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.istAktiv}
                  onChange={(e) => setFormData({ ...formData, istAktiv: e.target.checked })}
                />
              }
              label="Job aktiv"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.jobName}>
            {editingJob ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ausführungshistorie Dialog */}
      <Dialog
        open={executionsDialogOpen}
        onClose={() => setExecutionsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon />
              Ausführungshistorie: {selectedJobForExecutions?.jobName}
            </Box>
            <Tooltip title="Aktualisieren">
              <IconButton
                size="small"
                onClick={() => selectedJobForExecutions && handleShowExecutions(selectedJobForExecutions)}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingExecutions ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : executions.length === 0 ? (
            <Typography color="textSecondary" align="center" py={3}>
              Keine Ausführungen vorhanden
            </Typography>
          ) : (
            <List disablePadding>
              {executions.map((exec, index) => (
                <React.Fragment key={exec.executionId}>
                  {index > 0 && <Divider />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      py: 2,
                      bgcolor: exec.status === 'Failed' ? 'error.50' : 'transparent'
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" mr={2} pt={0.5}>
                      {getStatusIcon(exec.status)}
                    </Box>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(exec.startZeit)}
                          </Typography>
                          <Chip
                            size="small"
                            label={exec.status === 'Success' ? 'Erfolgreich' : exec.status === 'Failed' ? 'Fehlgeschlagen' : exec.status}
                            color={
                              exec.status === 'Success'
                                ? 'success'
                                : exec.status === 'Failed'
                                ? 'error'
                                : 'default'
                            }
                          />
                          <Typography variant="caption" color="textSecondary">
                            Dauer: {formatDuration(exec.dauerMs)}
                          </Typography>
                          {exec.verarbeiteteElemente !== undefined && exec.verarbeiteteElemente > 0 && (
                            <Chip
                              size="small"
                              label={`${exec.verarbeiteteElemente} verarbeitet`}
                              variant="outlined"
                              color="primary"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          {exec.fehlerMeldung && (
                            <Alert severity="error" sx={{ mb: 1, py: 0 }}>
                              <Typography variant="body2">{exec.fehlerMeldung}</Typography>
                            </Alert>
                          )}
                          {exec.ergebnis && (
                            <Box
                              component="pre"
                              sx={{
                                m: 0,
                                p: 1.5,
                                bgcolor: 'grey.100',
                                borderRadius: 1,
                                overflow: 'auto',
                                maxHeight: 200,
                                fontSize: '0.75rem',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {(() => {
                                try {
                                  return JSON.stringify(JSON.parse(exec.ergebnis), null, 2);
                                } catch {
                                  return exec.ergebnis;
                                }
                              })()}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="textSecondary" sx={{ flex: 1, pl: 2 }}>
            {executions.length} Ausführung(en) angezeigt
          </Typography>
          <Button onClick={() => setExecutionsDialogOpen(false)}>Schliessen</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ScheduledJobsVerwaltung;
